import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  GraduationCap, 
  Plus, 
  Trash2, 
  Edit3, 
  Play, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Award,
  ChevronRight,
  ChevronLeft,
  RotateCcw,
  FileQuestion,
  Loader2,
  AlertCircle,
  Trophy,
  Target,
  MapPin,
  Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function QuizPanel({ tourId, isOpen, onClose, onStartInteractiveQuiz, pointerIntersection, currentSweepId, cameraPosition }) {
  const { token, user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isTaking, setIsTaking] = useState(false);
  
  // Location capture state (spacebar method - same as hotspots)
  const [capturingQuestionIndex, setCapturingQuestionIndex] = useState(null);
  const latestPositionRef = useRef(null);
  
  // Track latest pointer position for spacebar capture
  useEffect(() => {
    if (pointerIntersection?.position) {
      latestPositionRef.current = pointerIntersection.position;
    }
  }, [pointerIntersection]);
  
  // Handle spacebar capture (same as hotspots)
  useEffect(() => {
    if (capturingQuestionIndex === null) return;
    
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        
        if (!currentSweepId) {
          alert('No sweep position available. Navigate inside the 3D tour first.');
          return;
        }
        
        console.log('=== SPACEBAR CAPTURE FOR QUIZ ===');
        console.log('currentSweepId:', currentSweepId);
        console.log('cameraPosition:', JSON.stringify(cameraPosition));
        console.log('cameraPosition.rotation:', JSON.stringify(cameraPosition?.rotation));
        
        // Build update object with all captured data at once
        // This ensures all data is captured atomically from the current moment
        const capturedData = {
          target_sweep_id: currentSweepId,
          target_rotation: null,
          target_position: null,
        };
        
        // Capture camera rotation - THIS IS CRITICAL for navigation
        if (cameraPosition?.rotation) {
          capturedData.target_rotation = {
            x: Number(cameraPosition.rotation.x),
            y: Number(cameraPosition.rotation.y)
          };
          console.log('Captured rotation:', JSON.stringify(capturedData.target_rotation));
        } else {
          console.warn('No camera rotation available at capture time!');
        }
        
        // Capture position if available (optional, for click detection)
        if (latestPositionRef.current) {
          capturedData.target_position = {
            x: latestPositionRef.current.x,
            y: latestPositionRef.current.y,
            z: latestPositionRef.current.z,
          };
        }
        
        // Apply all updates in a single state update
        // This prevents race conditions between multiple updateQuestion calls
        setQuizForm(prev => ({
          ...prev,
          questions: prev.questions.map((q, i) => {
            if (i === capturingQuestionIndex) {
              const updatedQuestion = { 
                ...q, 
                target_sweep_id: capturedData.target_sweep_id,
                target_rotation: capturedData.target_rotation,
                target_position: capturedData.target_position,
              };
              console.log('Updated question with captured data:', JSON.stringify({
                target_sweep_id: updatedQuestion.target_sweep_id,
                target_rotation: updatedQuestion.target_rotation,
              }));
              return updatedQuestion;
            }
            return q;
          })
        }));
        
        // Exit capture mode
        setCapturingQuestionIndex(null);
        
        const rotStr = capturedData.target_rotation 
          ? `(${capturedData.target_rotation.x.toFixed(1)}°, ${capturedData.target_rotation.y.toFixed(1)}°)`
          : 'not captured';
        alert(`Location captured!\nSweep: ${currentSweepId.substring(0,12)}...\nView: ${rotStr}`);
      }
      
      if (e.code === 'Escape') {
        setCapturingQuestionIndex(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [capturingQuestionIndex, currentSweepId, cameraPosition]);
  
  // Quiz form state
  const [quizForm, setQuizForm] = useState({
    title: '',
    description: '',
    passing_score: 70,
    time_limit_minutes: null,
    allow_retakes: true,
    shuffle_questions: false,
    show_correct_answers: true,
    questions: []
  });
  
  // Quiz taking state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [attempts, setAttempts] = useState([]);
  
  const isAdmin = user?.role === 'admin' || user?.role === 'editor';

  const fetchQuizzes = useCallback(async () => {
    if (!tourId || !token) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/elearning/quizzes/tour/${tourId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuizzes(data);
      }
    } catch (err) {
      console.error('Failed to fetch quizzes:', err);
    } finally {
      setLoading(false);
    }
  }, [tourId, token]);

  useEffect(() => {
    if (isOpen) {
      fetchQuizzes();
    }
  }, [isOpen, fetchQuizzes]);

  const handleCreateQuiz = async () => {
    console.log('=== CREATING QUIZ ===');
    console.log('quizForm:', JSON.stringify(quizForm, null, 2));
    console.log('Questions with rotation:', quizForm.questions?.map(q => ({
      question: q.question?.substring(0, 30),
      target_sweep_id: q.target_sweep_id,
      target_rotation: q.target_rotation
    })));
    
    try {
      const payload = {
        tour_id: tourId,
        ...quizForm
      };
      console.log('Sending CREATE payload:', JSON.stringify(payload, null, 2));
      
      const res = await fetch(`${API_URL}/api/elearning/quizzes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const responseData = await res.json();
      console.log('Create response:', responseData);
      
      if (res.ok) {
        await fetchQuizzes();
        setIsEditing(false);
        resetForm();
      }
    } catch (err) {
      console.error('Failed to create quiz:', err);
    }
  };

  const handleUpdateQuiz = async () => {
    if (!selectedQuiz) return;
    
    console.log('=== SAVING QUIZ ===');
    console.log('quizForm:', JSON.stringify(quizForm, null, 2));
    console.log('Questions with rotation:', quizForm.questions?.map(q => ({
      question: q.question?.substring(0, 30),
      target_sweep_id: q.target_sweep_id,
      target_rotation: q.target_rotation
    })));
    
    try {
      const payload = {
        tour_id: tourId,
        ...quizForm
      };
      console.log('Sending payload:', JSON.stringify(payload, null, 2));
      
      const res = await fetch(`${API_URL}/api/elearning/quizzes/${selectedQuiz.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const responseData = await res.json();
      console.log('Save response:', responseData);
      
      if (res.ok) {
        alert('Quiz saved successfully!');
        await fetchQuizzes();
        setIsEditing(false);
        setSelectedQuiz(null);
        resetForm();
      } else {
        alert(`Failed to save quiz: ${responseData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to update quiz:', err);
      alert(`Error saving quiz: ${err.message}`);
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!confirm('Are you sure you want to delete this quiz?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/elearning/quizzes/${quizId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        await fetchQuizzes();
      }
    } catch (err) {
      console.error('Failed to delete quiz:', err);
    }
  };

  // SCORM Export handler
  const handleScormExport = async (quizId, quizTitle) => {
    try {
      // Fetch the SCORM package as a blob
      const res = await fetch(`${API_URL}/api/scorm/export/${quizId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to generate SCORM package');
      }
      
      // Get the blob and create download link
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Clean filename
      const safeName = (quizTitle || 'Quiz').replace(/[^a-zA-Z0-9\s-_]/g, '').trim().replace(/\s+/g, '_');
      a.download = `${safeName}_SCORM.zip`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Failed to export SCORM:', err);
      alert('Failed to export SCORM package. Please try again.');
    }
  };

  const startQuiz = async (quiz) => {
    console.log('QuizPanel.startQuiz called:', { 
      quizTitle: quiz?.title,
      questions: quiz?.questions?.length,
      hasOnStartInteractiveQuiz: !!onStartInteractiveQuiz
    });
    
    // Check if quiz has interactive_click questions
    const hasInteractiveQuestions = quiz.questions?.some(q => 
      q.question_type === 'interactive_click' || q.question_type === 'location_click'
    );
    
    // Check if ANY question has a location set - if so, use the interactive overlay for navigation
    const hasLocationQuestions = quiz.questions?.some(q => q.target_sweep_id);
    
    console.log('Quiz check:', { hasInteractiveQuestions, hasLocationQuestions });
    
    if ((hasInteractiveQuestions || hasLocationQuestions) && onStartInteractiveQuiz) {
      console.log('Using interactive quiz overlay');
      // Close the dialog and start interactive quiz in 3D viewer (handles navigation)
      onClose?.();
      onStartInteractiveQuiz(quiz);
      return;
    }
    
    console.log('Using regular quiz flow (no locations)');
    // Regular quiz flow for quizzes without locations
    setSelectedQuiz(quiz);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setQuizResult(null);
    setIsTaking(true);
    
    if (quiz.time_limit_minutes) {
      setTimeRemaining(quiz.time_limit_minutes * 60);
    }
    
    // Fetch previous attempts
    try {
      const res = await fetch(`${API_URL}/api/elearning/quizzes/${quiz.id}/attempts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAttempts(data);
      }
    } catch (err) {
      console.error('Failed to fetch attempts:', err);
    }
  };

  const submitQuiz = async () => {
    if (!selectedQuiz) return;
    
    try {
      const res = await fetch(`${API_URL}/api/elearning/quizzes/${selectedQuiz.id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ answers })
      });
      
      if (res.ok) {
        const result = await res.json();
        setQuizResult(result);
      }
    } catch (err) {
      console.error('Failed to submit quiz:', err);
    }
  };

  const resetForm = () => {
    setQuizForm({
      title: '',
      description: '',
      passing_score: 70,
      time_limit_minutes: null,
      allow_retakes: true,
      shuffle_questions: false,
      show_correct_answers: true,
      questions: []
    });
  };

  const editQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setQuizForm({
      title: quiz.title,
      description: quiz.description || '',
      passing_score: quiz.passing_score,
      time_limit_minutes: quiz.time_limit_minutes,
      allow_retakes: quiz.allow_retakes,
      shuffle_questions: quiz.shuffle_questions,
      show_correct_answers: quiz.show_correct_answers,
      questions: quiz.questions || []
    });
    setIsEditing(true);
  };

  const addQuestion = () => {
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, {
        id: `q_${Date.now()}`,
        question: '',
        question_type: 'multiple_choice',
        options: ['', '', '', ''],
        correct_answer: '',
        explanation: '',
        points: 1
      }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const removeQuestion = (index) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === questionIndex) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          return { ...q, options: newOptions };
        }
        return q;
      })
    }));
  };

  // Timer effect
  useEffect(() => {
    if (timeRemaining !== null && timeRemaining > 0 && isTaking && !quizResult) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            submitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeRemaining, isTaking, quizResult]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            Quizzes & Assessments
          </DialogTitle>
          <DialogDescription>
            Create and manage quizzes for training validation
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {isTaking && selectedQuiz ? (
            // Quiz Taking View
            <div className="h-full flex flex-col">
              {quizResult ? (
                // Results View
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  {quizResult.passed ? (
                    <>
                      <Trophy className="h-16 w-16 text-yellow-500 mb-4" />
                      <h3 className="text-2xl font-bold text-green-600 mb-2">Congratulations!</h3>
                      <p className="text-muted-foreground mb-4">You passed the quiz!</p>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-16 w-16 text-red-500 mb-4" />
                      <h3 className="text-2xl font-bold text-red-600 mb-2">Not Quite</h3>
                      <p className="text-muted-foreground mb-4">You did not reach the passing score.</p>
                    </>
                  )}
                  
                  <div className="bg-muted rounded-lg p-6 w-full max-w-sm mb-6">
                    <div className="text-4xl font-bold mb-2">{quizResult.score.toFixed(1)}%</div>
                    <div className="text-sm text-muted-foreground">
                      Passing Score: {selectedQuiz.passing_score}%
                    </div>
                    {quizResult.time_taken_seconds && (
                      <div className="text-sm text-muted-foreground mt-2">
                        Time: {formatTime(quizResult.time_taken_seconds)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {selectedQuiz.allow_retakes && !quizResult.passed && (
                      <Button onClick={() => startQuiz(selectedQuiz)}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => {
                      setIsTaking(false);
                      setQuizResult(null);
                      setSelectedQuiz(null);
                    }}>
                      Back to Quizzes
                    </Button>
                  </div>
                </div>
              ) : (
                // Question View
                <>
                  <div className="flex items-center justify-between p-4 border-b">
                    <div>
                      <h3 className="font-semibold">{selectedQuiz.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Question {currentQuestionIndex + 1} of {selectedQuiz.questions.length}
                      </p>
                    </div>
                    {timeRemaining !== null && (
                      <Badge variant={timeRemaining < 60 ? "destructive" : "secondary"}>
                        <Clock className="h-3 w-3 mr-1" />
                        {formatTime(timeRemaining)}
                      </Badge>
                    )}
                  </div>
                  
                  <Progress 
                    value={(currentQuestionIndex + 1) / selectedQuiz.questions.length * 100} 
                    className="h-1"
                  />
                  
                  <ScrollArea className="flex-1 p-6">
                    {selectedQuiz.questions[currentQuestionIndex] && (
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-medium mb-4">
                            {selectedQuiz.questions[currentQuestionIndex].question}
                          </h4>
                          
                          {selectedQuiz.questions[currentQuestionIndex].question_type === 'multiple_choice' && (
                            <RadioGroup
                              value={answers[selectedQuiz.questions[currentQuestionIndex].id] || ''}
                              onValueChange={(value) => setAnswers(prev => ({
                                ...prev,
                                [selectedQuiz.questions[currentQuestionIndex].id]: value
                              }))}
                            >
                              {selectedQuiz.questions[currentQuestionIndex].options.map((option, i) => (
                                <div key={i} className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                  <RadioGroupItem value={option} id={`option-${i}`} />
                                  <Label htmlFor={`option-${i}`} className="flex-1 cursor-pointer">
                                    {option}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          )}
                          
                          {selectedQuiz.questions[currentQuestionIndex].question_type === 'true_false' && (
                            <RadioGroup
                              value={answers[selectedQuiz.questions[currentQuestionIndex].id] || ''}
                              onValueChange={(value) => setAnswers(prev => ({
                                ...prev,
                                [selectedQuiz.questions[currentQuestionIndex].id]: value
                              }))}
                            >
                              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <RadioGroupItem value="true" id="true" />
                                <Label htmlFor="true" className="flex-1 cursor-pointer">True</Label>
                              </div>
                              <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <RadioGroupItem value="false" id="false" />
                                <Label htmlFor="false" className="flex-1 cursor-pointer">False</Label>
                              </div>
                            </RadioGroup>
                          )}
                        </div>
                      </div>
                    )}
                  </ScrollArea>
                  
                  <div className="flex items-center justify-between p-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentQuestionIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Previous
                    </Button>
                    
                    {currentQuestionIndex === selectedQuiz.questions.length - 1 ? (
                      <Button onClick={submitQuiz}>
                        Submit Quiz
                        <CheckCircle2 className="h-4 w-4 ml-2" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setCurrentQuestionIndex(prev => 
                          Math.min(selectedQuiz.questions.length - 1, prev + 1)
                        )}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : isEditing ? (
            // Edit/Create View
            <ScrollArea className="h-[60vh]">
              <div className="space-y-6 p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Quiz Title</Label>
                    <Input
                      value={quizForm.title}
                      onChange={(e) => setQuizForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Enter quiz title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Passing Score (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={quizForm.passing_score}
                      onChange={(e) => setQuizForm(prev => ({ ...prev, passing_score: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={quizForm.description}
                      onChange={(e) => setQuizForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what this quiz covers"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Time Limit (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="180"
                      value={quizForm.time_limit_minutes || ''}
                      onChange={(e) => setQuizForm(prev => ({ 
                        ...prev, 
                        time_limit_minutes: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                      placeholder="No limit"
                    />
                    <p className="text-xs text-muted-foreground">Leave empty for unlimited time</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="allow-retakes"
                      checked={quizForm.allow_retakes}
                      onCheckedChange={(checked) => setQuizForm(prev => ({ ...prev, allow_retakes: checked }))}
                    />
                    <Label htmlFor="allow-retakes">Allow Retakes</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-answers"
                      checked={quizForm.show_correct_answers}
                      onCheckedChange={(checked) => setQuizForm(prev => ({ ...prev, show_correct_answers: checked }))}
                    />
                    <Label htmlFor="show-answers">Show Correct Answers</Label>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-lg font-semibold">Questions</Label>
                    <Button size="sm" onClick={addQuestion}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Question
                    </Button>
                  </div>
                  
                  {quizForm.questions.map((question, qIndex) => (
                    <Card key={question.id} className="p-4">
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <Label>Question {qIndex + 1}</Label>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeQuestion(qIndex)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                        
                        <Textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                          placeholder="Enter your question"
                          rows={2}
                        />
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Question Type</Label>
                            <Select
                              value={question.question_type}
                              onValueChange={(value) => updateQuestion(qIndex, 'question_type', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                                <SelectItem value="true_false">True/False</SelectItem>
                                <SelectItem value="interactive_click">Interactive Click (3D)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Points</Label>
                            <Input
                              type="number"
                              min="1"
                              value={question.points}
                              onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value))}
                            />
                          </div>
                        </div>

                        {/* Interactive Click Question - Target position */}
                        {question.question_type === 'interactive_click' && (
                          <div className="space-y-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                            <div className="flex items-center gap-2">
                              <Target className="h-4 w-4 text-emerald-500" />
                              <Label className="text-emerald-700 dark:text-emerald-400">Interactive Click Target</Label>
                            </div>
                            
                            <div className="space-y-2">
                              <Label>Target Name/Description</Label>
                              <Input
                                value={question.correct_answer || ''}
                                onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value)}
                                placeholder="e.g., Fire Extinguisher, Emergency Stop Button"
                              />
                              <p className="text-xs text-muted-foreground">
                                This is shown to the user as what they need to find.
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label>Target Position & Camera View</Label>
                              {capturingQuestionIndex === qIndex ? (
                                <div className="p-4 bg-blue-500/20 border-2 border-blue-500 rounded-lg animate-pulse">
                                  <div className="text-center space-y-2">
                                    <div className="text-blue-600 font-medium">📍 Capture Mode Active</div>
                                    <p className="text-sm">Navigate to the desired view in the 3D tour</p>
                                    <p className="text-sm font-bold">Press SPACE or ENTER to capture</p>
                                    <p className="text-xs text-muted-foreground">Press ESC to cancel</p>
                                  </div>
                                </div>
                              ) : question.target_sweep_id ? (
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">
                                      Sweep: {question.target_sweep_id.substring(0, 12)}...
                                    </Badge>
                                    {question.target_rotation && (
                                      <Badge variant="outline" className="bg-blue-500/10 text-blue-600">
                                        View: ({question.target_rotation.x?.toFixed(1)}°, {question.target_rotation.y?.toFixed(1)}°)
                                      </Badge>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        updateQuestion(qIndex, 'target_position', null);
                                        updateQuestion(qIndex, 'target_sweep_id', null);
                                        updateQuestion(qIndex, 'target_rotation', null);
                                      }}
                                      className="h-6 text-xs text-red-500 hover:text-red-600"
                                    >
                                      Clear
                                    </Button>
                                  </div>
                                  {!question.target_rotation && (
                                    <span className="text-xs text-amber-500">⚠ No camera view angle saved - re-capture location</span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setCapturingQuestionIndex(qIndex)}
                                    className="border-emerald-500/50 text-emerald-600 hover:bg-emerald-500/10"
                                  >
                                    <MapPin className="h-4 w-4 mr-1" />
                                    Set Location (Spacebar)
                                  </Button>
                                  <span className="text-xs text-muted-foreground">
                                    Click, navigate to view, press SPACE
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label>Click Tolerance (meters)</Label>
                              <Input
                                type="number"
                                min="0.5"
                                max="10"
                                step="0.5"
                                value={question.click_radius || 2}
                                onChange={(e) => updateQuestion(qIndex, 'click_radius', parseFloat(e.target.value))}
                                className="w-24"
                              />
                              <p className="text-xs text-muted-foreground">
                                How close the user must click to the target (default: 2m)
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Question Location - For ALL question types (not just interactive) */}
                        {question.question_type !== 'interactive_click' && (
                          <div className="space-y-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-blue-500" />
                              <Label className="text-blue-700 dark:text-blue-400">Question Location (Optional)</Label>
                            </div>
                            
                            {/* Spacebar capture mode - same as Interactive Click questions */}
                            {capturingQuestionIndex === qIndex ? (
                              <div className="p-4 bg-blue-500/20 border-2 border-blue-500 rounded-lg animate-pulse">
                                <div className="text-center space-y-2">
                                  <div className="text-blue-600 font-medium">📍 Capture Mode Active</div>
                                  <p className="text-sm">Navigate to the desired view in the 3D tour</p>
                                  <p className="text-sm font-bold">Press SPACE or ENTER to capture</p>
                                  <p className="text-xs text-muted-foreground">Press ESC to cancel</p>
                                </div>
                              </div>
                            ) : question.target_sweep_id ? (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                                    <MapPin className="h-3 w-3 mr-1" />
                                    Location saved!
                                  </Badge>
                                  {question.target_rotation && (
                                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                                      View: ({question.target_rotation.x?.toFixed(1)}°, {question.target_rotation.y?.toFixed(1)}°)
                                    </Badge>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      updateQuestion(qIndex, 'target_position', null);
                                      updateQuestion(qIndex, 'target_sweep_id', null);
                                      updateQuestion(qIndex, 'target_rotation', null);
                                    }}
                                    className="h-6 text-xs text-red-500 hover:text-red-600"
                                  >
                                    Clear
                                  </Button>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Sweep: {question.target_sweep_id.substring(0, 12)}...
                                </div>
                                {!question.target_rotation && (
                                  <span className="text-xs text-amber-500">⚠ No camera view angle - re-capture for precise navigation</span>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCapturingQuestionIndex(qIndex)}
                                  className="mt-2 border-blue-500/50 text-blue-600 hover:bg-blue-500/10"
                                >
                                  <MapPin className="h-4 w-4 mr-1" />
                                  Re-capture (Spacebar)
                                </Button>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                <p className="text-xs text-muted-foreground">
                                  Click button, navigate to view, press SPACE to capture
                                </p>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setCapturingQuestionIndex(qIndex)}
                                  className="border-blue-500/50 text-blue-600 hover:bg-blue-500/10"
                                >
                                  <MapPin className="h-4 w-4 mr-1" />
                                  Set Location (Spacebar)
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {question.question_type === 'multiple_choice' && (
                          <div className="space-y-2">
                            <Label>Options</Label>
                            {question.options.map((option, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <Input
                                  value={option}
                                  onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                  placeholder={`Option ${oIndex + 1}`}
                                />
                                <Checkbox
                                  checked={question.correct_answer === option}
                                  onCheckedChange={() => updateQuestion(qIndex, 'correct_answer', option)}
                                />
                                <span className="text-xs text-muted-foreground">Correct</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {question.question_type === 'true_false' && (
                          <div className="space-y-2">
                            <Label>Correct Answer</Label>
                            <Select
                              value={question.correct_answer}
                              onValueChange={(value) => updateQuestion(qIndex, 'correct_answer', value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select correct answer" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label>Explanation (shown after answer)</Label>
                          <Textarea
                            value={question.explanation || ''}
                            onChange={(e) => updateQuestion(qIndex, 'explanation', e.target.value)}
                            placeholder="Explain why this is the correct answer"
                            rows={2}
                          />
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </ScrollArea>
          ) : (
            // Quiz List View
            <ScrollArea className="h-[60vh]">
              <div className="space-y-4 p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : quizzes.length === 0 ? (
                  <div className="text-center py-8">
                    <FileQuestion className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium mb-2">No Quizzes Yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Create quizzes to assess training comprehension
                    </p>
                    {isAdmin && (
                      <Button onClick={() => setIsEditing(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create First Quiz
                      </Button>
                    )}
                  </div>
                ) : (
                  quizzes.map((quiz) => (
                    <Card key={quiz.id} className="hover:bg-muted/50 transition-colors">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base">{quiz.title}</CardTitle>
                            {quiz.description && (
                              <CardDescription className="mt-1">{quiz.description}</CardDescription>
                            )}
                          </div>
                          <Badge variant="outline">
                            {quiz.questions?.length || 0} questions
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>Pass: {quiz.passing_score}%</span>
                          {quiz.time_limit_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {quiz.time_limit_minutes} min
                            </span>
                          )}
                          {quiz.allow_retakes && (
                            <span className="flex items-center gap-1">
                              <RotateCcw className="h-3 w-3" />
                              Retakes allowed
                            </span>
                          )}
                        </div>
                      </CardContent>
                      <CardFooter className="pt-2">
                        <div className="flex items-center gap-2 w-full flex-wrap">
                          <Button 
                            size="sm" 
                            onClick={() => startQuiz(quiz)}
                            disabled={quiz.questions?.length === 0}
                            className={
                              quiz.questions?.some(q => q.question_type === 'interactive_click' || q.target_sweep_id) 
                                ? 'bg-emerald-600 hover:bg-emerald-500' 
                                : ''
                            }
                          >
                            <Play className="h-4 w-4 mr-2" />
                            {quiz.questions?.some(q => q.question_type === 'interactive_click' || q.target_sweep_id) 
                              ? 'Start 3D Quiz' 
                              : 'Start Quiz'}
                          </Button>
                          {isAdmin && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => editQuiz(quiz)}>
                                <Edit3 className="h-4 w-4 mr-2" />
                                Edit
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleScormExport(quiz.id, quiz.title)}
                                title="Export as SCORM package for LMS"
                              >
                                <Download className="h-4 w-4 mr-2" />
                                SCORM
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteQuiz(quiz.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </CardFooter>
                    </Card>
                  ))
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => {
                setIsEditing(false);
                setSelectedQuiz(null);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button 
                onClick={selectedQuiz ? handleUpdateQuiz : handleCreateQuiz}
                disabled={!quizForm.title || quizForm.questions.length === 0}
              >
                {selectedQuiz ? 'Update Quiz' : 'Create Quiz'}
              </Button>
            </>
          ) : isTaking ? (
            <Button variant="outline" onClick={() => {
              setIsTaking(false);
              setSelectedQuiz(null);
              setQuizResult(null);
            }}>
              Exit Quiz
            </Button>
          ) : (
            <>
              {isAdmin && !loading && quizzes.length > 0 && (
                <Button onClick={() => setIsEditing(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Quiz
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuizPanel;
